import { memo, useCallback, type ComponentPropsWithoutRef, type MouseEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';

const DISALLOWED_MARKDOWN_ELEMENTS = ['img'];

function getSameOriginAppPath(href: string | undefined) {
  if (href == null) {
    return undefined;
  }

  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) {
      return undefined;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

function AvaAIMarkdownLink({
  href,
  children,
  onLinkClick,
  interactiveLinks = true,
}: ComponentPropsWithoutRef<'a'> & { onLinkClick?: () => void; interactiveLinks?: boolean }) {
  const navigate = useNavigate();
  const appPath = getSameOriginAppPath(href);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      if (
        appPath == null ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      onLinkClick?.();
      navigate(appPath);
    },
    [appPath, navigate, onLinkClick]
  );

  if (!interactiveLinks || appPath == null) {
    return <span title={href}>{children}</span>;
  }

  return (
    <a href={href} onClick={handleClick} data-testid="ava-ai-in-app-link">
      {children}
    </a>
  );
}

/** Memoized so settled messages are not re-parsed on every streaming delta of the message being generated. */
export const AvaAIMarkdown = memo(function AvaAIMarkdown({
  text,
  onLinkClick,
  interactiveLinks = true,
}: {
  text: string;
  onLinkClick?: () => void;
  /** When false, links render as text so markdown can sit inside button rows. */
  interactiveLinks?: boolean;
}) {
  return (
    <ReactMarkdown
      disallowedElements={DISALLOWED_MARKDOWN_ELEMENTS}
      skipHtml
      components={{
        a: props => <AvaAIMarkdownLink {...props} onLinkClick={onLinkClick} interactiveLinks={interactiveLinks} />,
      }}
    >
      {text}
    </ReactMarkdown>
  );
});
